// The Production roster on the Authority screen (CLAUDE.md rule 28, migration 026).
//
// Who works in Production according to Employment Hero, their line, the @majans.com address EH
// holds for them (if any), and whether they hold CAPTURE / VERIFY now. It answers "who still needs a grant"; it is
// never evidence of who did a check (attribution is the token's, rule 2). "Grant" only pre-fills
// the existing form on the page (above the roster since UX-51); the grant itself is the same POST /authorisations, checked the same way.
//
// Rule 15 throughout: a failed read is "Not known" with a way to ask again, a roster never read is
// not an empty plant, and a refresh that failed after a good snapshot is said beside it.
import { useMemo, useState } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { api } from '../../lib/api';
import { actionLabel, actionWithCode } from '../../lib/vocabulary';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/DataTable';
import { Saved } from '@/components/Saved';
import { localDate, serverMessage, useResource, type MyGrants } from '../../shells/OfficeShell';

export type RosterGrantAction = 'CAPTURE' | 'VERIFY';

interface RosterGrant {
  action: RosterGrantAction;
  effectiveFrom: string;
  effectiveTo: string | null;
}

export interface RosterPerson {
  ehEmployeeId: string;
  name: string;
  line: string;
  locationPath: string;
  startDate: string | null;
  upn: string | null;
  grants: RosterGrant[];
}

export interface RosterView {
  snapshot: { id: number; takenAt: string; memberCount: number; sourceQueriedOn: string | null } | null;
  lastAttempt: { at: string; ok: boolean; reason: string | null } | null;
  stale: boolean;
  people: RosterPerson[];
  counts: { active: number; withMicrosoftAccount: number; holdingCapture: number; holdingVerify: number; holdingEither: number };
}

/** What the panel says about the roster's freshness, or null when there is nothing to warn of. */
export function rosterWarning(v: RosterView): string | null {
  const failedAfter =
    v.lastAttempt && !v.lastAttempt.ok && (!v.snapshot || Date.parse(v.lastAttempt.at) > Date.parse(v.snapshot.takenAt));
  const why = v.lastAttempt?.reason ? ` (${v.lastAttempt.reason})` : '';
  if (!v.snapshot) {
    return failedAfter
      ? `No roster has been read yet: the last attempt, ${localDate(v.lastAttempt!.at) ?? v.lastAttempt!.at}, failed${why}. Who works in Production is not known here.`
      : 'No roster has been read yet. Who works in Production is not known here.';
  }
  if (failedAfter) {
    return `The last refresh failed${why}. Showing the roster read ${localDate(v.snapshot.takenAt) ?? v.snapshot.takenAt}.`;
  }
  if (v.stale) return `This roster was read ${localDate(v.snapshot.takenAt) ?? v.snapshot.takenAt} and may be out of date.`;
  return null;
}

/**
 * What the account column says when EH holds no @majans.com address (UI/UX audit, 31 Oct 2026,
 * UX-06). It used to say "No Microsoft account", and the panel added that such a person "cannot
 * sign in, so cannot be granted". Both claims were about Entra, and the roster never asks Entra:
 * the match is an exact UPN from an address EH holds (rule 28). Jamie Haigh read "No Microsoft
 * account" while the Register on the same page showed jamie@ holding CAPTURE and VERIFY. An
 * exact-match miss proves only that EH's record has no such address (rule 15), so that is all the
 * label says.
 */
export const NO_EH_ADDRESS = 'No @majans.com address in Employment Hero';

/** One in-force grant from the register, as Authority passes it down. */
export interface InForceGrant {
  upn: string;
  action: string;
}

/** A holder of CAPTURE or VERIFY whom no roster row names, with what they hold. */
export interface UnmatchedHolder {
  upn: string;
  actions: RosterGrantAction[];
}

/**
 * The CAPTURE / VERIFY holders the roster matched to nobody (UX-06). The other half of the
 * "No @majans.com address" cell: a person EH holds no address for may well be one of these, and
 * listing them is how a reader joins the two by eye, never by the app guessing (matching stays
 * exact UPN, rule 28: a fuzzy name match would put one person's grant on another's row).
 * Only the two actions the roster's columns show; a RELEASE or AUTH_ADMIN holder is QA or the
 * office, and listing them under a Production roster would be noise. Shared accounts
 * (production@) are listed too: they hold grants and are nobody in EH, which is worth seeing.
 */
export function unmatchedHolders(people: RosterPerson[], inForce: InForceGrant[]): UnmatchedHolder[] {
  const matched = new Set(people.map((p) => p.upn?.trim().toLowerCase()).filter((u): u is string => !!u));
  const byUpn = new Map<string, Set<RosterGrantAction>>();
  for (const g of inForce) {
    if (g.action !== 'CAPTURE' && g.action !== 'VERIFY') continue;
    const upn = g.upn.trim().toLowerCase();
    if (matched.has(upn)) continue;
    const set = byUpn.get(upn) ?? new Set<RosterGrantAction>();
    set.add(g.action);
    byUpn.set(upn, set);
  }
  return [...byUpn.entries()]
    .map(([upn, set]) => ({ upn, actions: (['CAPTURE', 'VERIFY'] as const).filter((a) => set.has(a)) }))
    .sort((a, b) => a.upn.localeCompare(b.upn));
}

function held(p: RosterPerson, a: RosterGrantAction) {
  return p.grants.find((g) => g.action === a) ?? null;
}

const col = createColumnHelper<RosterPerson>();

export function ProductionRoster({
  grants,
  onGrant,
  inForce,
}: {
  grants: MyGrants;
  onGrant: (upn: string, action: RosterGrantAction) => void;
  /** The register's in-force grants, or null while it has not answered (UX-06; rule 15). */
  inForce: InForceGrant[] | null;
}) {
  const mayRead = grants.holds('AUTH_ADMIN') || grants.holds('RELEASE');
  const mayGrant = grants.holds('AUTH_ADMIN');
  const roster = useResource<RosterView>(mayRead ? '/roster' : null);
  const [syncing, setSyncing] = useState(false);
  // A refresh that landed is a Saved line (audit finding 15); one that did not is a warning.
  const [saved, setSaved] = useState<string | null>(null);
  const [syncNote, setSyncNote] = useState<string | null>(null);

  const columns = useMemo(() => {
    const grantCell = (a: RosterGrantAction) =>
      col.accessor((p) => (held(p, a) ? 'held' : p.upn ? 'not held' : '—'), {
        id: a,
        // Words with the code (UI/UX audit, 31 Oct 2026, UX-17): the Grant shortcut beside it
        // pre-fills the form, whose picker names the same code.
        header: actionWithCode(a),
        meta: { nowrap: true },
        cell: (c) => {
          const p = c.row.original;
          const g = held(p, a);
          if (g) {
            return (
              <span className="font-semibold text-(--ok)">
                Held{g.effectiveTo ? ` to ${localDate(g.effectiveTo) ?? g.effectiveTo}` : ''}
              </span>
            );
          }
          if (!p.upn) return <span className="text-(--muted)">—</span>;
          return (
            <Button
              variant="outline"
              size="sm"
              disabled={!mayGrant}
              title={mayGrant ? `Fill the grant form with ${p.upn} and ${a}` : 'Granting needs a current AUTH_ADMIN authorisation.'}
              onClick={() => onGrant(p.upn!, a)}
            >
              Grant {a}
            </Button>
          );
        },
      });
    return [
      col.accessor('name', { header: 'Name', meta: { rowHeader: true } }),
      col.accessor('line', { header: 'Line', meta: { nowrap: true } }),
      // "From EH" in the header (UX-06): the address is Employment Hero's, matched exactly; it is
      // not a read of Entra, so the column does not claim to know who has a Microsoft account.
      col.accessor((p) => p.upn ?? NO_EH_ADDRESS, {
        id: 'account',
        header: 'Account (from EH)',
        cell: (c) =>
          c.row.original.upn ? (
            c.getValue()
          ) : (
            <span className="text-(--muted)">{NO_EH_ADDRESS}</span>
          ),
      }),
      grantCell('CAPTURE'),
      grantCell('VERIFY'),
    ];
  }, [mayGrant, onGrant]);

  async function refresh() {
    setSyncing(true);
    setSyncNote(null);
    setSaved(null);
    try {
      const r = (await api.post('/roster/sync', {})) as { ok: boolean; members?: number; reason?: string };
      if (r.ok) setSaved(`Roster refreshed: ${r.members} people read from Employment Hero.`);
      else setSyncNote(`The refresh failed (${r.reason}). Nothing was changed.`);
    } catch (err: unknown) {
      setSyncNote(serverMessage(err));
    } finally {
      setSyncing(false);
      roster.reload();
    }
  }

  let body;
  if (!grants.known) {
    body = <p className="m-0 text-(length:--fs-sm) text-(--muted)">{grants.error ? 'Your authorisations could not be read, so the roster is not shown.' : 'Reading your authorisations…'}</p>;
  } else if (!mayRead) {
    body = <p className="m-0 text-(length:--fs-sm) text-(--muted)">The roster needs a current {actionLabel('AUTH_ADMIN')} (AUTH_ADMIN) or {actionLabel('RELEASE')} (RELEASE) authorisation.</p>;
  } else if (roster.error) {
    body = (
      <div className="flex flex-wrap items-center gap-(--s3)">
        <p role="alert" className="m-0 text-(length:--fs-sm) font-semibold text-(--warn)">
          Not known: the roster could not be read. {roster.error}
        </p>
        <Button variant="outline" size="sm" onClick={roster.reload}>Try again</Button>
      </div>
    );
  } else if (!roster.data) {
    body = <p className="m-0 text-(length:--fs-sm) text-(--muted)">Reading the roster…</p>;
  } else {
    const v = roster.data;
    const warning = rosterWarning(v);
    // Null while the register is unread: then nothing is said about unmatched holders at all,
    // rather than an empty list that reads as "every holder is on the roster" (rule 15).
    const unmatched = v.snapshot && inForce ? unmatchedHolders(v.people, inForce) : null;
    body = (
      <>
        {warning ? (
          <p role="status" className="m-0 text-(length:--fs-sm) font-semibold text-(--warn)">{warning}</p>
        ) : null}
        {v.snapshot ? (
          <>
            <p className="m-0 text-(length:--fs-sm) text-(--muted)">
              {/* No "with a Microsoft account" count (UX-06): it counted EH addresses, not accounts. */}
              {v.counts.active} active · {v.counts.holdingCapture} hold {actionLabel('CAPTURE')} · {v.counts.holdingVerify} hold {actionLabel('VERIFY')}.
              Read {localDate(v.snapshot.takenAt) ?? v.snapshot.takenAt}.
            </p>
            <DataTable
              data={v.people}
              columns={columns}
              caption="Active Production staff from Employment Hero"
              getRowId={(p) => p.ehEmployeeId}
              // 25 a page (UI/UX audit, 31 Oct 2026, UX-51): 75 rows unpaged made Authority 4809px
              // long. Search still covers every row, not only the page shown.
              pageSize={25}
              search="Search name, line or account"
              loading={roster.loading}
            />
            {inForce === null ? (
              <p className="m-0 text-(length:--fs-xs) text-(--muted)">
                Grant holders not matched to Employment Hero: not known until the register below has been read.
              </p>
            ) : unmatched && unmatched.length > 0 ? (
              <div className="flex flex-col gap-(--s1)">
                <h3 className="m-0 text-(length:--fs-sm) font-bold">Granted, not matched to Employment Hero</h3>
                <p className="m-0 text-(length:--fs-xs) text-(--muted)">
                  These accounts hold {actionLabel('CAPTURE')} or {actionLabel('VERIFY')} now, and no roster row has that exact address. One may be a
                  person shown above as "{NO_EH_ADDRESS}", or a shared account.
                </p>
                <ul className="m-0 list-disc pl-(--s5) text-(length:--fs-sm)">
                  {unmatched.map((u) => (
                    <li key={u.upn}>
                      <span className="font-semibold">{u.upn}</span> · {u.actions.map(actionLabel).join(', ')}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        ) : null}
      </>
    );
  }

  return (
    <section aria-label="Production roster" className="rounded-(--radius) border border-(--border) bg-(--card) shadow-card p-(--s4)">
      <div className="flex flex-wrap items-baseline justify-between gap-(--s3)">
        <h2 className="m-0 font-display text-[22px] font-semibold leading-tight">Production roster</h2>
        {mayGrant ? (
          <Button variant="outline" size="sm" onClick={refresh} disabled={syncing}>
            {syncing ? 'Reading…' : 'Refresh now'}
          </Button>
        ) : null}
      </div>
      <p className="m-0 mt-(--s1) text-(length:--fs-xs) text-(--muted)">
        From Employment Hero, read daily. It shows who may still need a grant; it is never a record of who did a check.
        A person is matched to an account only by the exact @majans.com address EH holds for them.
      </p>
      <div className="mt-(--s3) flex flex-col gap-(--s2)">
        {saved ? <Saved>{saved}</Saved> : null}
        {syncNote ? (
          <p role="alert" className="m-0 text-(length:--fs-sm) font-semibold text-(--warn)">
            {syncNote}
          </p>
        ) : null}
        {body}
      </div>
    </section>
  );
}
